import CommandBarRowIcon from '@/components/commandBar/CommandBarRowIcon';
import Input from '@/components/Input';
import Kbd from '@/components/Kbd';
import { FiSearch } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import IconV2 from '@/components/v2/IconV2';
import useChangeCompany from '@/hooks/useChangeCompany';
import useCommandBar from '@/hooks/useCommandBar';
import setPath from '@/lib/setPath';
import { sendAiChatMessage, toggleAiChat } from '@/redux/actions/uiActions/aiChatActions';
import { addRecentAction } from '@/redux/actions/userActions';
import selectors from '@/redux/selectors/selectors';
import { FloatingFocusManager, useClick, useDismiss, useFloating, useId, useInteractions, useRole } from '@floating-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ChevronTitle from './ChevronTitle';

const WORKFLOW_PATH = (workflowId, workflowVersion = 'published') => `/workflows/${workflowId}/${workflowVersion}`;
const JOB_PATH = (jobId) => `/jobs/${jobId}/logs`;
const APP_PATH = (appId, version) => `/apps/${appId}/${version}`;
const ACTIONS_PATH = (appId, version, actionId) => `${APP_PATH(appId, version)}/actions/${actionId}`;

const typeLabelMap = {
  actions: 'App Action',
  companies: 'Organization',
  jobs: 'Job',
  workflows: 'Workflow',
  apps: 'App',
  pages: 'Page'
};

const CommandBar = () => {
  const recentActions = useSelector(selectors.recentActions);
  const selectedCompany = useSelector(selectors.userSelectedCompany);

  const [selectedGroup, setSelectedGroup] = useState(0);
  const [selectedItem, setSelectedItem] = useState(0);

  const selectedRef = useRef(null);
  const inputRef = useRef(null);
  const contentRef = useRef(null);

  const { open, onOpenChange, searchQuery, handleSearchQueryChange, isLoading, data } = useCommandBar();
  const changeCompany = useChangeCompany();

  const { refs, context } = useFloating({ open, onOpenChange });
  const labelId = useId();
  const descriptionId = useId();
  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context);
  const { getFloatingProps } = useInteractions([click, dismiss, role]);

  const suggestionGroups = useMemo(() => {
    const groups = [];

    groups.push({
      title: 'Suggestions',
      items: [
        //
        ...(data || [])?.filter((item) => item?.primaryKey !== selectedCompany?.id),
        { type: 'ai', title: 'Ask AI about...' }
      ]
    });

    if (recentActions?.length > 0 && searchQuery.length === 0) {
      groups.push({
        title: 'Recent',
        items: recentActions?.slice(0, 4)
      });
    }

    return groups;
  }, [data, recentActions]);

  const titleGetter = useCallback((item) => {
    if (item.type === 'actions') {
      return <ChevronTitle left={item.meta.appName} right={`Edit ${item.title}`} />;
    }

    if (item.type === 'companies') {
      return (
        <span>
          Switch to <b>{item.title}</b> organization
        </span>
      );
    }

    if (item.type === 'jobs' || item.type === 'workflows') {
      if (selectedCompany?.name !== item.meta.companyName) {
        return <ChevronTitle left={item.meta.companyName} right={item.title} />;
      }
      return `${item.title}`;
    }

    if (item.type === 'apps') {
      return `Open ${item.title} app`;
    }

    return item.title;
  }, []);

  const actions = useMemo(
    () => ({
      workflows: (item) => {
        setPath(WORKFLOW_PATH(item.primaryKey, item?.meta?.publishedVersion));
      },
      jobs: (item) => {
        setPath(JOB_PATH(item.primaryKey));
      },
      pages: (item) => {
        setPath(item?.meta?.route);
      },
      apps: (item) => {
        setPath(APP_PATH(item.meta.appId, item.meta.version));
      },
      actions: (item) => {
        setPath(ACTIONS_PATH(item.meta.appId, item.meta.version, item.primaryKey));
      },
      companies: (item) => {
        changeCompany(item.primaryKey);
      },
      ai: () => {
        if (searchQuery.length > 0) {
          sendAiChatMessage(searchQuery);
        } else {
          toggleAiChat({ open: true });
        }
      }
    }),
    [setPath, changeCompany]
  );

  const handleClick = useCallback(
    (type, item) => {
      actions?.[type]?.(item);
      if (type !== 'ai') {
        addRecentAction(item);
      }
      onOpenChange(false);
    },
    [actions, onOpenChange, addRecentAction]
  );

  const handleAskAi = () => {
    if (searchQuery.length > 0) {
      const item = suggestionGroups.find((group) => group.items.find((item) => item.type === 'ai'))?.items.find((item) => item.type === 'ai');
      if (item) handleClick('ai', item);
    }
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (!suggestionGroups?.length) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          e.stopPropagation();
          if (selectedItem < suggestionGroups[selectedGroup].items.length - 1) {
            setSelectedItem((prev) => prev + 1);
          } else if (selectedGroup < suggestionGroups.length - 1) {
            setSelectedGroup((prev) => prev + 1);
            setSelectedItem(0);
          } else {
            setSelectedGroup(0);
            setSelectedItem(0);
          }
          selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          e.stopPropagation();
          if (selectedItem > 0) {
            setSelectedItem((prev) => prev - 1);
          } else if (selectedGroup > 0) {
            setSelectedGroup((prev) => prev - 1);
            setSelectedItem(suggestionGroups[selectedGroup - 1].items.length - 1);
          } else {
            setSelectedGroup(suggestionGroups.length - 1);
            setSelectedItem(suggestionGroups[suggestionGroups.length - 1].items.length - 1);
          }
          selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
        case 'Enter': {
          e.preventDefault();
          e.stopPropagation();
          const group = suggestionGroups[selectedGroup];
          const item = group.items[selectedItem];

          if (item) {
            handleClick(item.type, item);
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          onOpenChange(false);
          break;
        }
        case 'Tab': {
          e.preventDefault();
          handleAskAi();
          break;
        }
        default:
          break;
      }
    },
    [suggestionGroups, selectedGroup, selectedItem, handleClick, onOpenChange]
  );

  const handleOpen = () => {
    const group = suggestionGroups[selectedGroup];
    const item = group.items[selectedItem];

    if (item) {
      handleClick(item.type, item);
    }
  };

  useEffect(() => {
    setSelectedGroup(0);
    setSelectedItem(0);
  }, [searchQuery]);

  useEffect(() => {
    if (open === false) {
      setSelectedGroup(0);
      setSelectedItem(0);
    } else {
      inputRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <FloatingFocusManager context={context}>
      <div className="command-bar-container" data-loading={isLoading} ref={refs.setFloating} aria-labelledby={labelId} aria-describedby={descriptionId} {...getFloatingProps()} onKeyDown={handleKeyDown}>
        <div className="command-bar-header">
          <Input ref={inputRef} icon={FiSearch} iconClassName="search-icon" className="command-bar-input" placeholder="What do you want to do?" value={searchQuery} onChange={handleSearchQueryChange} />
          <p className="ask-ai" onClick={handleAskAi} role="button" tabIndex={0}>
            Ask AI <Kbd>Tab</Kbd>
          </p>
        </div>

        <div ref={contentRef} className="command-bar-content" data-has-search={searchQuery.length > 0} data-loading={isLoading}>
          {isLoading && (
            <div className="command-bar-content-item">
              <div className="command-bar-content-item-subitem loading">
                <IconV2 icon={Spinner} className="light" />
                Fetching results...
              </div>
            </div>
          )}
          {!isLoading &&
            suggestionGroups.map((group, groupIndex) => (
              <div className="command-bar-content-item" key={`${group.title}-${groupIndex}`}>
                {!searchQuery?.length && group?.title && <p className="title">{!searchQuery.length && group.title}</p>}
                {group.items.map((item, itemIndex) => (
                  <div
                    ref={selectedGroup === groupIndex && selectedItem === itemIndex ? selectedRef : null}
                    data-selected={selectedGroup === groupIndex && selectedItem === itemIndex}
                    data-title={item.type === 'companies' ? `Switch to ${item.title} Organization` : ''}
                    className={`command-bar-content-item-subitem`}
                    key={`${item.uniqueKey || item.primaryKey || itemIndex}-${itemIndex}`}
                    onClick={() => handleClick(item.type, item)}
                    tabIndex={-1} // Make the div focusable
                  >
                    <CommandBarRowIcon row={item} />
                    {item?.type === 'ai' && searchQuery?.length > 0 && (
                      <span>
                        Ask AI about "<b>{searchQuery}</b>"
                      </span>
                    )}
                    {!(item?.type === 'ai' && searchQuery?.length > 0) && <>{titleGetter(item)}</>}
                    <p className="type">{typeLabelMap[item.type]}</p>
                  </div>
                ))}
              </div>
            ))}
        </div>
        <div className="command-bar-footer">
          <p onClick={handleOpen} role="button" tabIndex={0}>
            Open <Kbd>↩</Kbd>
          </p>
        </div>
      </div>
    </FloatingFocusManager>
  );
};

export default CommandBar;
